<template>
    <div>
        <div class="modal-overlay active"  @click="closeDialog()"></div>
        <div class="modal">
            <div class="modal__header">
                <h2 id="emailModalTitle" class="headline-s">New User</h2>
                <CloseIcon @click="closeDialog()" class="modal__header--close" alt="Close Modal" aria-label="Close" />
            </div>
            <div class="modal__body flex-column authorization-form">
                <div class="notification notification-error" v-show="errors.email">
                    <p class="paragraph-details"><strong>Error:</strong> {{ errors.email }}</p>
                </div>
                <div class="input-folder">
                    <div class="input-folder--input">
                        <PrimeInputText 
                            v-model="form.username" 
                            id="username" 
                            placeholder=" " 
                            required
                            :class="{'error': form.errors.username, 'invalid-state-input': form.errors.username}"
                        />
                        <label for="username">
                            <p class="paragraph-default">User Name *</p>
                        </label>
                    </div>
                    <div class="input-folder--support">
                        <p class="input-support-text invalid-state" v-if="form.errors.username"> {{ form.errors.username }}</p>
                    </div>
                </div>

                <EmailInput v-model="form.email" :errors="form.errors.email"  placeholder="Email *" id="email"/>

                <PasswordInput v-model="form.password" :errors="passwordTests"  placeholder="Password *" id="password"/>

                <PasswordInput v-model="form.confirm_password" :errors="form.errors.confirm_password" placeholder="Confirm Password *" id="confirm_password"/>
                
                <div class="field-state input-folder">
                    <custom-select
                        v-model="accountLevelComputed"
                        label="Account Level *"
                        name="account_level" 
                        id="account_level" 
                        aria-label="Account Level"
                        :options="accountLevels"
                        :error="form.errors.account_level"
                    />
                    <div class="input-folder--support">
                        <p class="input-support-text invalid-state" v-if="form.errors.account_level"> {{ form.errors.account_level }}</p>
                    </div>
                </div>
                
                <div class="flex-row justify-end">
                    <RothcoCta :disabled="form.processing" class="primary" @click="addUser">Add User</RothcoCta>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts" setup>
    import { ref, computed, inject, } from "vue";
    import { useForm } from "@/helpers/composables/useForm";
    import { object, string, number} from 'yup';
    // import { route } from "ziggy-js";
    import RothcoCta from "@/components/RothcoCta.vue";
    import EmailInput from "@/components/EmailInput.vue";
    import PasswordInput from "@/components/PasswordInput.vue";
    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";
    import CloseIcon from "@icons/closeIcon.svg";
    const dialogRef = inject('dialogRef');
    let route = inject('route');
    let settingsStore = useSettingsStore();
    let { dealer_no } = storeToRefs(settingsStore);
    const closeDialog = () => {
        dialogRef?.value?.close({ addedUser: false});
    }
    let accountLevels = computed(()=>{
        return [
            {key: 1, value: 'Administrator'},
            {key: 2, value: 'See Pricing'},
            {key: 3, value: 'Place Orders'},
            {key: 4, value: 'Basic Display Only'}
        ];
    })
    const form = useForm({
        useInertia: false,
        schema: object({
            account_level: number().required("Account Level is required").test('is_valid_level', 
                'Invalid account level', 
                (value) => {
                    return accountLevels.value.map(level=>level.key).includes(value)
                },
            ),
            username: string().required("Username is required"),
            email: string().email('Must be a valid email').required("Email is required"),
            password: string()
                .required('The password field is required')
                .test('password-strength', '', (value, ctx) => {
                    if (!value) return true;
                    let messages = [];
                    let weakPasswordMessage;
                    if (!/[A-Z]/.test(value)) messages.push('at least one uppercase letter');
                    if (!/[a-z]/.test(value)) messages.push('at least one lowercase letter');
                    if (!/[0-9]/.test(value)) messages.push('at least one number');
                    if (!/[#?!@$%^&*-]/.test(value)) messages.push('at least one special character (# ? ! @ $  % ^ & * -)');
                    if (/\s/.test(value)) messages.push('no spaces');
                    if (value.length < 8) messages.push('at least 8 characters');
                    if (/password/i.test(value)) weakPasswordMessage = 'Your password is too weak.';
                    if (messages.length || weakPasswordMessage) return ctx.createError({ message: messages.length > 0 ? `Password must contain ${messages.join(', ')}.${weakPasswordMessage ? " " + weakPasswordMessage : ""}` : weakPasswordMessage });
                    return true;
                })
                .default(""),
            confirm_password: string().required('The confirm password field is required').test('passwords_match', 'Password confirmation does not match', (value) => {
                return value === form.password;
            }).default("")
        })
    });

    const accountLevelComputed = computed({
        get(){
            return accountLevels.value.find(level=>level.key == form.account_level) ?? 0;
        },
        set(newVal){
            form.account_level = newVal.key;
        }
    })

    let passwordTests = computed(()=>{
       let passwordErrors = Object.keys(form.errors).filter((key) => {
           return key.includes('password') && key !== 'confirm_password';
       }).map((key) => {
           return form.errors[key];
       });
       return passwordErrors.length ? [passwordErrors[0]] : [];
    })    

    let { setNotificationMessage } = useSettingsStore();

    async function addUser(){
        if(form.processing) return;
        
        await form.validate({ all: true });
        if(Object.keys(form.errors).length > 0) return;
        form.post(route('endpoint.user.store'), {
            user: form.transform((data)=>({
                ...data,
                rothco_dealer_no: dealer_no.value,
            }))
        }, {
            beforeFetch(){
                errors.value = {
                    email: null
                }
            },
            onFetchError(ctx){
                if(ctx.data.error){
                    errors.value = ctx.data.error;
                }
                return ctx;
            },
            afterFetch(ctx){
                if(ctx.data.error){
                    errors.value = ctx.data.error;
                }
                else{
                    const user = ctx.data;
                    setNotificationMessage({
                        message: `User ${user.username} created successfully`,
                        type: 'success'
                    })
                    dialogRef?.value?.close({ addedUser: true, })
                }
            }
        })
    }

    let errors = ref({
        email: null
    });


</script>
